import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Services from "./components/Services";
import Industries from "./components/Industries";
import BuildingBlocks from "./components/BuildingBlocks";
import Benefits from "./components/Benefits";
import CleaningProcess from "./components/CleaningProcess";
import Guarantees from "./components/Guarantees";
import Contact from "./components/Contact";
import Datenschutz from "./components/Datenschutz";
import Impressum from "./components/Impressum";
import ServicePage from "./components/ServicePage";
import CookieConsent from "./components/CookieConsent";
import SEO from "./components/SEO";
import ServiceArea from "./components/ServiceArea";
import { areaServed, homeDescription, homeKeywords, homeTitle } from "./data/serviceArea";
import { company } from "./data/company";
import services from "./data/services";

import "./App.css";

// Structured Data (JSON-LD) for LocalBusiness
const structuredData = {
  "@context": "https://schema.org",
  "@type": ["CleaningService", "LocalBusiness"],
  "@id": "https://www.mfd-berlin.com/#organization",
  "name": company.legalName,
  "alternateName": "M.F.D. Gebäudereinigung",
  "legalName": company.legalName,
  "url": "https://www.mfd-berlin.com/",
  "logo": "https://www.mfd-berlin.com/mfdlogo.png",
  "image": [
    "https://www.mfd-berlin.com/logomfd.jpg",
    "https://www.mfd-berlin.com/mfdlogo.png"
  ],
  "description": homeDescription,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": company.street,
    "addressLocality": company.city,
    "postalCode": company.postalCode,
    "addressRegion": "Berlin",
    "addressCountry": "DE"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": company.lat,
    "longitude": company.lng
  },
  "telephone": company.phones.map((phone) => phone.tel),
  "email": company.email,
  "priceRange": "$$",
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "06:00",
      "closes": "18:00"
    }
  ],
  "areaServed": areaServed,
  "serviceArea": {
    "@type": "GeoCircle",
    "geoMidpoint": {
      "@type": "GeoCoordinates",
      "latitude": company.lat,
      "longitude": company.lng
    },
    "geoRadius": 40000
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Gebäudereinigung Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Unterhaltsreinigung",
          "description": "Tägliche oder wöchentliche Reinigung für Büros und Arbeitsplätze"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Glas- & Fassadenreinigung",
          "description": "Streifenfreie Glasflächen innen & außen, Fassadenreinigung mit Hubarbeitsbühne"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Baureinigung",
          "description": "Kostenlose Vor-Ort-Begehung, Entfernung von Bauschutt, Staub & Rückständen"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Grundreinigung",
          "description": "Tiefenreinigung schwer zugänglicher Bereiche, effektive Entfernung hartnäckiger Verschmutzungen"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Teppichreinigung",
          "description": "Tiefenreinigung bis in die Fasern, materialschonende Behandlung aller Teppicharten"
        }
      }
    ]
  },
  "knowsLanguage": "de"
};

structuredData.hasOfferCatalog.itemListElement = services.map((service) => ({
  "@type": "Offer",
  itemOffered: {
    "@type": "Service",
    name: service.title,
    description: service.detailText,
    url: `https://www.mfd-berlin.com/leistungen/${service.slug}`,
    areaServed: "Berlin und Umgebung",
  },
}));

function ScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        setTimeout(() => target.scrollIntoView({ behavior: "smooth" }), 50);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollManager />
      {/* Structured Data (JSON-LD) */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Navbar />

      <Routes>
        {/* Homepage route */}
        <Route
          path="/"
          element={
            <>
              <SEO
                title={homeTitle}
                description={homeDescription}
                keywords={homeKeywords}
              />
              <Hero />
              <Services />
              <Industries />
              <BuildingBlocks />
              <Benefits />
              <CleaningProcess />
              <Guarantees />
              <ServiceArea />
              <Contact />
            </>
          }
        />

        {/* Datenschutz route */}
        <Route path="/datenschutz" element={<Datenschutz />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/leistungen/:slug" element={<ServicePage />} />
      </Routes>

      <CookieConsent />

      <footer className="footer">
        <p className="footer-copy">
          © 2026. M.F.D & Co. G.M.B.H.{" "}
          <span className="footer-credit">
            Design by:{" "}
            <a
              href="https://www.s3le.com.hr"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-400"
            >
              S3le Web Design
            </a>{" "}
            <span className="footer-rights">Alle Rechte vorbehalten.</span>
          </span>
        </p>
        <p className="footer-links">
          <a href="/impressum">Impressum</a>
          <a href="/datenschutz">Datenschutz</a>
        </p>
      </footer>
    </BrowserRouter>
  );
}

export default App;
